import moment, { Moment } from 'moment-timezone'
import {
  DateRangeType,
  resolveDateRangeTypeWindow,
} from 'src/components/DateFilterDropdown/dateRangeType'
import type { BillPaymentDisplayStatus } from 'src/queries/payments/payments.types'

export interface PaymentHistoryUrlState {
  search: string
  statuses: BillPaymentDisplayStatus[]
  vendorIds: string[] | null
  dateRangeType: DateRangeType.Last30Days | DateRangeType.Custom
  since: string
  until: string
  page: number
}

const OWNED_PARAMS = [
  'search',
  'status',
  'vendorId',
  'dateRange',
  'from',
  'to',
  'page',
]
const STATUSES: BillPaymentDisplayStatus[] = [
  'ACTION_REQUIRED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
]

export const defaultPaymentHistoryUrlState = (
  now?: Moment,
): PaymentHistoryUrlState => {
  const range = resolveDateRangeTypeWindow({
    dateRangeType: DateRangeType.Last30Days,
    now,
  })!
  return {
    search: '',
    statuses: [],
    vendorIds: null,
    dateRangeType: DateRangeType.Last30Days,
    since: range.since.format('YYYY-MM-DD'),
    until: range.until.format('YYYY-MM-DD'),
    page: 0,
  }
}

const validLocalDate = (value: string | null): value is string =>
  value !== null && moment(value, 'YYYY-MM-DD', true).isValid()

export const parsePaymentHistoryUrlState = (
  search: string,
  now?: Moment,
): PaymentHistoryUrlState => {
  const defaults = defaultPaymentHistoryUrlState(now)
  const params = new URLSearchParams(search)
  const from = params.get('from')
  const to = params.get('to')
  const isValidCustomRange =
    params.get('dateRange') === 'custom' &&
    validLocalDate(from) &&
    validLocalDate(to) &&
    !moment(from, 'YYYY-MM-DD', true).isAfter(
      moment(to, 'YYYY-MM-DD', true),
      'day',
    )
  const requestedStatuses = new Set(params.getAll('status'))
  const statuses = STATUSES.filter((status) => requestedStatuses.has(status))
  const vendorIds = params.has('vendorId')
    ? [
        ...new Set(
          params
            .getAll('vendorId')
            .map((value) => value.trim().slice(0, 200))
            .filter(Boolean),
        ),
      ]
        .sort()
        .slice(0, 100)
    : null
  const pageValue = params.get('page')
  const page = pageValue && /^\d+$/.test(pageValue) ? Number(pageValue) : 0

  return {
    search: (params.get('search') ?? '').slice(0, 200),
    statuses,
    vendorIds,
    dateRangeType: isValidCustomRange
      ? DateRangeType.Custom
      : DateRangeType.Last30Days,
    since: isValidCustomRange ? from : defaults.since,
    until: isValidCustomRange ? to : defaults.until,
    page: Number.isSafeInteger(page) ? page : 0,
  }
}

export const serializePaymentHistoryUrlState = (
  currentSearch: string,
  state: PaymentHistoryUrlState,
): string => {
  const params = new URLSearchParams(currentSearch)
  OWNED_PARAMS.forEach((key) => params.delete(key))
  if (state.search) params.set('search', state.search)
  STATUSES.filter((status) => state.statuses.includes(status)).forEach(
    (status) => params.append('status', status),
  )
  if (state.vendorIds !== null) {
    if (state.vendorIds.length === 0) params.append('vendorId', '')
    else
      [...new Set(state.vendorIds)]
        .sort()
        .forEach((vendorId) => params.append('vendorId', vendorId))
  }
  if (state.dateRangeType === DateRangeType.Custom) {
    params.set('dateRange', 'custom')
    params.set('from', state.since)
    params.set('to', state.until)
  }
  if (state.page > 0) params.set('page', String(state.page))
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}
